import {
    AppLayout,
    AppLayoutProps,
    Button,
    Container,
    GridProps,
    PropertyFilterProps,
    SpaceBetween,
    TableProps,
    TextContent,
} from "@amzn/awsui-components-react";
import { NonCancelableEventHandler } from "@amzn/awsui-components-react/polaris/internal/events";
import { Order } from "@amzn/ito-client";
import CustomGridLayout from "common/component/CustomGridLayout/CustomGridLayout";
import CustomPopOver from "common/component/CustomPopOver/CustomPopOver";
import CustomPropertyFilter from "common/component/CustomPropertyFilter/CustomPropertyFilter";
import CheckboxFilter, { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";
import OrderItemsTable from "common/component/OrderItemsTable/OrderItemsTable";
import NavigationBreadcrumbs from "common/navigation/NavigationBreadcrumbs";
import Paths from "common/paths/paths";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { compressFilters, decompressFilters } from "common/utils/compress-url";
import { convertTokensToAndFilters, convertTokensToPropertyFilter } from "common/utils/property-filter-helper";
import { LOCAL_IT_ORDER_LIST } from "helpers";
import { useBuildingsWithParentChild } from "hooks/useBuildings";
import { useMainCategories } from "hooks/useCatalog";
import useFlashbar from "hooks/useFlashbar";
import useOrderResponse from "hooks/useOrderResponse";
import { buildingIdFormat, HardwareOrderStatus } from "interfaces";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { UserPermissions } from "services/user-permissions";

const getStringPath: string = "views.itOrderList";

/** Types */

/**
 * @interface
 * Represents the current sorting state for the order list table.
 */
export interface SortingState {
    sortBy?: string;
    sortOrder?: string;
    sortField?: string;
}

/**
 * @interface
 * Basic field filter for simple key-value pairs
 */
interface FieldFilter {
    [key: string]: string | string[];
}

/**
 * @interface
 * MongoDB-style OR operator containing array of field filters or nested OR operators
 */
interface OrOperator {
    $or: (FieldFilter | OrOperator)[];
}

/**
 * @interface
 * Query object structure for filtering orders with MongoDB-style operators.
 */
interface QueryObject {
    [key: string]: string | string[] | (FieldFilter | OrOperator)[] | undefined;
    $and?: (FieldFilter | OrOperator)[];
    $or?: (FieldFilter | OrOperator)[];
    status?: string[];
    "requested.by"?: string[];
    asc?: string[];
    desc?: string[];
}

/**
 * @interface
 * Groups location selections by type (region, country, city, building).
 */
interface LocationGroup {
    [key: string]: string[];
}

const getStringPathStatuses: string = "common.orderStatuses";

/**
 * Array of checkbox options for order status filtering with localized names
 */
export const ORDER_STATUS_OPTIONS: CheckboxNode[] = [
    { id: HardwareOrderStatus.ASSIGNED, name: getString(`${getStringPathStatuses}.assigned`), selected: true },
    {
        id: HardwareOrderStatus.APPROVED,
        name: getString(`${getStringPathStatuses}.approved`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.WORK_IN_PROGRESS,
        name: getString(`${getStringPathStatuses}.workInProgress`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_QUOTE,
        name: getString(`${getStringPathStatuses}.pendingQuote`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_REQ_INFO,
        name: getString(`${getStringPathStatuses}.pendingReqInfo`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_APPROVAL,
        name: getString(`${getStringPathStatuses}.pendingApproval`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.PENDING_DELIVERY,
        name: getString(`${getStringPathStatuses}.pendingDelivery`),
        selected: true,
    },
    {
        id: HardwareOrderStatus.CANCELLED,
        name: getString(`${getStringPathStatuses}.cancelled`),
    },
    {
        id: HardwareOrderStatus.DELIVERED,
        name: getString(`${getStringPathStatuses}.delivered`),
    },
    { id: HardwareOrderStatus.REJECTED, name: getString(`${getStringPathStatuses}.rejected`) },
];

/** Constants */
export const SEARCH_ORDERS_PROPERTY_KEYS = {
    poNumber: "items.subLineItems.poNumber",
    orderId: "orderReference",
    legacyOrderId: "details.legacyOrderId",
    requestCategories: "details.requestCategories",
    itemName: "display",
};

const PROPERTY_TO_URL_PARAM_MAP: Record<string, string> = {
    "items.subLineItems.poNumber": "po",
    orderReference: "order",
    "details.legacyOrderId": "legacy",
    "details.requestCategories": "category",
    display: "item",
    "details.deliverTo.region": "region",
    "details.deliverTo.country": "country",
    "details.deliverTo.city": "city",
    "details.deliverTo.building": "building",
};

const SORT_FIELD_MAPPINGS: Record<string, string> = {
    requestDate: "requested.on",
    statusHistory: "statusHistory.on",
};

const LOCATION_KEYS = ["region", "country", "city", "building"];

const FILTERING_PROPERTIES = [
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.poNumber,
        propertyLabel: "PO Number",
        operators: ["="],
        groupValuesLabel: "PO Number Values",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.orderId,
        propertyLabel: "Order ID",
        operators: ["="],
        groupValuesLabel: "Order ID Values",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.legacyOrderId,
        propertyLabel: "Legacy Order ID",
        operators: ["="],
        groupValuesLabel: "Legacy Order ID",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.requestCategories,
        propertyLabel: "Requested Category",
        operators: ["="],
        groupValuesLabel: "Request Categories",
    },
    {
        key: SEARCH_ORDERS_PROPERTY_KEYS.itemName,
        propertyLabel: "Item Name",
        operators: ["="],
        groupValuesLabel: "Item Names",
    },
];

const REQUESTER_FILTERING_PROPERTY = [
    {
        key: "requested.by",
        propertyLabel: "Requester",
        operators: ["="],
        groupValuesLabel: "Requester values",
    },
];

/** Auxiliary functions */

/**
 * Extracts primary selected locations from the hierarchical checkbox structure.
 * Traverses the tree to find all selected location nodes.
 *
 * @param locationSelectedItems - Array of checkbox nodes representing location hierarchy
 * @returns Array of selected location objects with building ID format
 */
const getPrimarySelectedLocations = (locationSelectedItems: CheckboxNode[]): buildingIdFormat[] => {
    const primaryLocations: buildingIdFormat[] = [];
    const queue = [...locationSelectedItems];

    while (queue.length > 0) {
        const location = queue.shift()!;

        if (location.selected) {
            primaryLocations.push(JSON.parse(decodeURIComponent(location.id)) as buildingIdFormat);
        } else if (location.children) {
            queue.push(...Array.from(location.children));
        }
    }

    return primaryLocations;
};

/**
 * Groups selected locations by their type (region, country, city, building).
 * This enables separate filtering by location hierarchy level.
 *
 * @param selectedLocations - Array of selected location objects
 * @returns Object with location names grouped by type
 */
const groupLocationsByType = (selectedLocations: buildingIdFormat[]): LocationGroup => {
    return selectedLocations.reduce((acc, location) => {
        acc[location.type] = acc[location.type] || [];
        acc[location.type].push(location.name);
        return acc;
    }, {} as LocationGroup);
};

/**
 * Creates MongoDB-style OR filters for location-based queries.
 * Each location type becomes a separate filter condition.
 *
 * @param locationGroups - Location names grouped by type
 * @returns Array of filter objects for MongoDB OR operations
 */
const createLocationFilters = (locationGroups: LocationGroup): Array<{ [key: string]: string[] }> => {
    const orFilters: Array<{ [key: string]: string[] }> = [];

    if (locationGroups.region?.length > 0) {
        orFilters.push({ "details.deliverTo.region": locationGroups.region });
    }
    if (locationGroups.country?.length > 0) {
        orFilters.push({ "details.deliverTo.country": locationGroups.country });
    }
    if (locationGroups.city?.length > 0) {
        orFilters.push({ "details.deliverTo.city": locationGroups.city });
    }
    if (locationGroups.building?.length > 0) {
        orFilters.push({ "details.deliverTo.building": locationGroups.building });
    }

    return orFilters;
};

/**
 * Extracts keyword tokens and creates search filters across all searchable fields.
 * Keywords without property keys are treated as global search terms.
 *
 * @param tokens - Property filter tokens from the search interface
 * @returns Object containing keyword filters and extracted keywords
 */
const createKeywordFilters = (tokens: ReadonlyArray<PropertyFilterProps.Token>) => {
    const keywords = tokens
        .filter((token) => !token.propertyKey && token.value.trim())
        .map((token) => token.value.trim());

    const keywordFilters = keywords.map((keyword) => ({
        $or: Object.values(SEARCH_ORDERS_PROPERTY_KEYS).map((key) => ({ [key]: keyword })),
    }));

    return { keywordFilters, keywords };
};

/**
 * Builds a comprehensive MongoDB-style query object from all filter options.
 * Combines property filters, keywords, location filters, and sorting parameters.
 *
 * @param filterOptions - All current filter selections and configurations
 * @param selectedLocations - Memoized selected locations to avoid recalculation
 * @param [currentSorting] - Current sorting state for the table
 * @returns Complete query object for the orders API
 */
const buildQueryObject = (
    filterOptions: ReturnType<typeof OrderFilteringOptions>,
    selectedLocations: buildingIdFormat[],
    currentSorting?: SortingState,
): QueryObject => {
    const queryObject: QueryObject = {};

    // Requester filter
    const requesters = filterOptions.requesterQuery.tokens
        .filter((token) => token.value?.length > 0)
        .map((token) => token.value.trim().toLowerCase());

    if (requesters.length > 0) {
        queryObject["requested.by"] = requesters;
    }

    // Status filter
    const selectedStatuses = filterOptions.orderStatus.filter((status) => status.selected).map((status) => status.id);

    if (selectedStatuses.length > 0) {
        queryObject.status = selectedStatuses;
    }

    // Sorting
    if (currentSorting?.sortOrder && currentSorting?.sortBy) {
        queryObject[currentSorting.sortOrder] = [currentSorting.sortBy];
    }

    // Handle property filters and keywords
    const { keywordFilters } = createKeywordFilters(filterOptions.query.tokens);
    const formattedQuery = convertTokensToPropertyFilter(filterOptions.query.tokens);

    // Location filters
    const locationGroups = groupLocationsByType(selectedLocations);
    const locationOrFilters = createLocationFilters(locationGroups);

    // Build query based on operation type
    if (filterOptions.query.operation === "and") {
        const andFilterObjects = convertTokensToAndFilters(filterOptions.query.tokens);
        const allAndFilters = [...andFilterObjects, ...keywordFilters];

        if (allAndFilters.length > 0) {
            queryObject.$and = allAndFilters;
        }

        if (locationOrFilters.length > 0) {
            queryObject.$or = locationOrFilters;
        }
    } else if (filterOptions.query.operation === "or") {
        const orderFilters = Object.keys(formattedQuery)
            .filter((key) => formattedQuery[key]?.length > 0)
            .map((key) => ({ [key]: formattedQuery[key] }));

        const andFilters = [];

        if (orderFilters.length > 0 || keywordFilters.length > 0) {
            andFilters.push({ $or: [...orderFilters, ...keywordFilters] });
        }

        if (locationOrFilters.length > 0) {
            andFilters.push({ $or: locationOrFilters });
        }

        if (andFilters.length > 0) {
            queryObject.$and = andFilters;
        }
    } else if (locationOrFilters.length > 0) {
        queryObject.$or = locationOrFilters;
    }

    return queryObject;
};

/**
 * Defines the maximum amount of characters until the url is compressed
 */
export const MAXIMUM_CHARACTERS_UNTIL_COMPRESSED = 500;

/**
 * Updates URL parameters to reflect current filter and sorting state.
 * Enables bookmarking and sharing of filtered views.
 *
 * @param filterOptions - All current filter selections and configurations
 * @param selectedLocations - Memoized selected locations to avoid recalculation
 * @param [currentSorting] - Current sorting state for the table
 * @returns URLSearchParams object with encoded filter state
 */
const updateUrlParams = (
    filterOptions: ReturnType<typeof OrderFilteringOptions>,
    selectedLocations: buildingIdFormat[],
    currentSorting?: SortingState,
): URLSearchParams => {
    const urlParams = new URLSearchParams();

    // Keywords
    const { keywords } = createKeywordFilters(filterOptions.query.tokens);
    if (keywords.length > 0) {
        urlParams.set("keywords", keywords.join(","));
    }

    // Requesters
    const requesters = filterOptions.requesterQuery.tokens
        .filter((token) => token.value?.length > 0)
        .map((token) => token.value.trim().toLowerCase());
    urlParams.set("requesters", requesters.join(","));

    // Status
    const selectedStatuses = filterOptions.orderStatus.filter((status) => status.selected).map((status) => status.id);
    urlParams.set("status", selectedStatuses.join(","));

    // Sorting
    if (currentSorting?.sortOrder && currentSorting?.sortBy) {
        urlParams.set("sortDirection", currentSorting.sortOrder);
        urlParams.set("sortBy", currentSorting.sortBy);
    }

    // Property filters
    const formattedQuery = convertTokensToPropertyFilter(filterOptions.query.tokens);
    Object.entries(formattedQuery).forEach(([key, values]) => {
        const urlKey = PROPERTY_TO_URL_PARAM_MAP[key] || key;
        if (values.length > 0) {
            urlParams.set(urlKey, values.join(","));
        }
    });

    urlParams.set("operation", filterOptions.query.operation);

    // Location filters
    const locationGroups = selectedLocations.reduce((acc, location) => {
        acc[location.type] = acc[location.type] || [];

        if (location.type === "city") {
            acc[location.type].push(`${location.name}|${location.root}`);
        } else {
            acc[location.type].push(location.name);
        }

        return acc;
    }, {} as LocationGroup);

    Object.entries(locationGroups).forEach(([type, names]) => {
        const urlKey = PROPERTY_TO_URL_PARAM_MAP[`details.deliverTo.${type}`];
        if (urlKey && names.length > 0) {
            urlParams.set(urlKey, names.join(","));
        }
    });

    /**
     * Compresses the URL when it surpasses the maximum characters
     * This avoids extremely long url that can prevent access to the page.
     */
    const paramsString = urlParams.toString();
    if (paramsString.length > MAXIMUM_CHARACTERS_UNTIL_COMPRESSED) {
        const compressed = new URLSearchParams();
        compressed.set("f", compressFilters(urlParams));
        return compressed;
    }

    return urlParams;
};

/**
 * Normalizes a property filter query for consistent comparison.
 * Sorts tokens by property key, value, and operator to ensure
 * queries with identical content but different token order are
 * considered equal.
 *
 * @param q - The property filter query to normalize
 * @returns Normalized query with sorted tokens
 */
export const normalizeQuery = (q: PropertyFilterProps.Query): PropertyFilterProps.Query => ({
    ...q,
    tokens: [...q.tokens].sort((a, b) => {
        const keyA = a.propertyKey ?? "";
        const keyB = b.propertyKey ?? "";
        const keyCompare = keyA.localeCompare(keyB);
        if (keyCompare !== 0) return keyCompare;

        const valueCompare = String(a.value).localeCompare(String(b.value));
        if (valueCompare !== 0) return valueCompare;

        return (a.operator || "").localeCompare(b.operator || "");
    }),
});

/**
 * Custom hook that provides URL parameter parsing functions for filter state.
 * Enables restoration of filter state from URL parameters on page load.
 *
 * @returns Object containing parser functions for different filter types
 */
export const useUrlFilterParsers = () => {
    const [searchParams] = useSearchParams();
    const REVERSE_URL_KEYS = Object.fromEntries(
        Object.entries(PROPERTY_TO_URL_PARAM_MAP).map(([key, value]) => [value, key]),
    );

    /**
     * Retrieves the actual url parameters of the website
     * Considers the possibility of compressed filters
     *
     * @returns The url search params
     */
    const getActualParams = (): URLSearchParams => {
        const compressed = searchParams.get("f");
        return compressed ? decompressFilters(compressed) : searchParams;
    };

    /**
     * Parses property filter tokens and operation from URL parameters.
     * Reconstructs the search query state from URL-friendly format.
     *
     * @returns Property filter query object with tokens and operation
     */
    const parseQueryFromUrl = (): PropertyFilterProps.Query => {
        const actualParams = getActualParams();
        const tokens: PropertyFilterProps.Token[] = [];
        const keywords = actualParams.get("keywords");

        if (keywords) {
            keywords.split(",").forEach((keyword) => {
                tokens.push({ value: keyword.trim(), operator: ":" });
            });
        }

        Object.entries(REVERSE_URL_KEYS).forEach(([urlKey, propertyKey]) => {
            if (LOCATION_KEYS.includes(urlKey as any)) return;

            const values = actualParams.get(urlKey);
            if (values) {
                values.split(",").forEach((value) => {
                    tokens.push({ propertyKey, value: value.trim(), operator: "=" });
                });
            }
        });

        const operation = actualParams.get("operation") === "or" ? "or" : "and";
        return { tokens, operation };
    };

    /**
     * Parses requester filter from URL parameters.
     * Defaults to current user if no requesters specified in URL.
     *
     * @returns Property filter query object for requester filtering
     */
    const parseRequesterFromUrl = (): PropertyFilterProps.Query => {
        const actualParams = getActualParams();
        const requesters = actualParams.get("requesters");
        const tokens: PropertyFilterProps.Token[] = [];

        if (requesters === null) {
            tokens.push({ value: UserPermissions.instance.getUsername, operator: ":" });
            return { tokens, operation: "and" };
        }

        if (requesters) {
            requesters.split(",").forEach((requester) => {
                if (requester.trim()) {
                    tokens.push({ value: requester.trim(), operator: ":" });
                }
            });
        }

        return { tokens, operation: "and" };
    };

    /**
     * Parses order status selections from URL parameters.
     * Defaults to all statuses selected if no status parameter in URL.
     *
     * @returns Array of checkbox nodes with selection state
     */
    const parseStatusFromUrl = (): CheckboxNode[] => {
        const actualParams = getActualParams();
        const statusParam = actualParams.get("status");

        if (statusParam === null) {
            return ORDER_STATUS_OPTIONS;
        }

        const selectedStatuses = statusParam ? statusParam.split(",") : [];
        return ORDER_STATUS_OPTIONS.map((status) => ({
            ...status,
            selected: selectedStatuses.includes(status.id),
        }));
    };

    /**
     * Parses location selections from URL parameters and applies to building hierarchy.
     * Handles hierarchical selection where parent selection affects children.
     *
     * @param buildingsData - Complete building hierarchy data
     * @returns Updated building hierarchy with selections applied
     */
    const parseLocationFromUrl = (buildingsData: CheckboxNode[]): CheckboxNode[] => {
        const actualParams = getActualParams();
        const hasLocationParams = LOCATION_KEYS.some((key) => actualParams.has(key));

        if (!hasLocationParams) {
            const unselectAll = (items: CheckboxNode[]): CheckboxNode[] =>
                items.map((item) => ({
                    ...item,
                    selected: false,
                    children: item.children ? unselectAll(Array.from(item.children)) : undefined,
                }));
            return unselectAll(buildingsData);
        }

        const selectedRegions = actualParams.get("region")?.split(",") || [];
        const selectedCountries = actualParams.get("country")?.split(",") || [];
        const selectedCities = actualParams.get("city")?.split(",") || [];
        const selectedBuildings = actualParams.get("building")?.split(",") || [];

        /**
         * Recursively updates location selection state in the hierarchy.
         *
         * @param items - Array of location nodes to update
         * @param [parentSelected=false] - Whether parent node is selected
         *
         * @returns Updated array with selection state applied
         */
        const updateLocationSelection = (items: CheckboxNode[], parentSelected = false): CheckboxNode[] => {
            return items.map((item) => {
                const locationData = JSON.parse(decodeURIComponent(item.id)) as buildingIdFormat;
                let isSelected = parentSelected;

                if (!isSelected) {
                    switch (locationData.type) {
                        case "region":
                            isSelected = selectedRegions.includes(locationData.name);
                            break;
                        case "country":
                            isSelected = selectedCountries.includes(locationData.name);
                            break;
                        case "city":
                            isSelected = selectedCities.some((cityEntry) => {
                                if (cityEntry.includes("|")) {
                                    const [cityName, rootPath] = cityEntry.split("|");
                                    return locationData.name === cityName && locationData.root === rootPath;
                                }
                                return locationData.name === cityEntry;
                            });
                            break;
                        case "building":
                            isSelected = selectedBuildings.includes(locationData.name);
                            break;
                    }
                }

                return {
                    ...item,
                    selected: isSelected,
                    children: item.children
                        ? updateLocationSelection(Array.from(item.children), isSelected)
                        : undefined,
                };
            });
        };

        return updateLocationSelection(buildingsData);
    };

    return {
        parseQueryFromUrl,
        parseRequesterFromUrl,
        parseStatusFromUrl,
        parseLocationFromUrl,
    };
};

/**
 * Custom hook that manages all filtering options and their state.
 * Provides centralized filter state management for the order list.
 *
 * @returns Object containing all filter options, state, and setter functions
 */
export const OrderFilteringOptions = () => {
    const urlFiltering = useUrlFilterParsers();
    const categories = useMainCategories();
    const buildingsData = useBuildingsWithParentChild();

    const categoryOptions: readonly PropertyFilterProps.FilteringOption[] | undefined = useMemo(() => {
        if (categories.isLoadingCat || categories.errorCat) {
            return undefined;
        }
        return categories.categories?.map((category) => ({
            propertyKey: SEARCH_ORDERS_PROPERTY_KEYS.requestCategories,
            value: category.name,
        }));
    }, [categories.categories, categories.isLoadingCat, categories.errorCat]);

    const [query, setQuery] = useState<PropertyFilterProps.Query>(() => urlFiltering.parseQueryFromUrl());
    const [requesterQuery, setRequesterQuery] = useState<PropertyFilterProps.Query>(() =>
        urlFiltering.parseRequesterFromUrl(),
    );
    const [orderStatus, setOrderStatus] = useState<CheckboxNode[]>(() => urlFiltering.parseStatusFromUrl());
    const [locationSelectedItems, setLocationSelectedItems] = useState<CheckboxNode[]>(() =>
        urlFiltering.parseLocationFromUrl(buildingsData),
    );

    const memoizedQuery = useMemo(() => {
        return normalizeQuery(query);
    }, [query]);

    return {
        filteringProperties: FILTERING_PROPERTIES,
        query: memoizedQuery,
        setQuery,
        orderStatusOptions: ORDER_STATUS_OPTIONS,
        requesterQuery,
        setRequesterQuery,
        requesterFilteringProperty: REQUESTER_FILTERING_PROPERTY,
        orderStatus,
        setOrderStatus,
        locationSelectedItems,
        setLocationSelectedItems,
        categoryOptions,
    };
};

/**
 * Main filtering hook that orchestrates all filter operations.
 * Manages URL synchronization, query building, and filter state coordination.
 *
 * @returns Object containing filter options, sorting handlers, and query state
 */
export const useFiltering = () => {
    const filterOptions = OrderFilteringOptions();
    const buildingsData = useBuildingsWithParentChild();
    const urlFiltering = useUrlFilterParsers();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const [query, setQuery] = useState<QueryObject>();
    const [filterLoading, setFilterLoading] = useState(true);
    const [currentSorting, setCurrentSorting] = useState<SortingState>();

    const memoizedGetPrimarySelectedLocations = useMemo(
        () => getPrimarySelectedLocations(filterOptions.locationSelectedItems),
        [filterOptions.locationSelectedItems],
    );

    // Sync URL changes with filter state
    useEffect(() => {
        if (filterLoading) return;

        const queryFromUrl = urlFiltering.parseQueryFromUrl();
        const requesterFromUrl = urlFiltering.parseRequesterFromUrl();
        const statusFromUrl = urlFiltering.parseStatusFromUrl();
        const locationFromUrl = urlFiltering.parseLocationFromUrl(buildingsData);

        const normalizedUrlQuery = normalizeQuery(queryFromUrl);
        const normalizedCurrentQuery = normalizeQuery(filterOptions.query);

        if (
            JSON.stringify(normalizedUrlQuery.tokens) !== JSON.stringify(normalizedCurrentQuery.tokens) ||
            normalizedUrlQuery.operation !== normalizedCurrentQuery.operation
        ) {
            filterOptions.setQuery(queryFromUrl);
        }

        if (JSON.stringify(requesterFromUrl.tokens) !== JSON.stringify(filterOptions.requesterQuery.tokens)) {
            filterOptions.setRequesterQuery(requesterFromUrl);
        }

        const currentStatusIds = filterOptions.orderStatus.filter((s) => s.selected).map((s) => s.id);
        const newStatusIds = statusFromUrl.filter((s) => s.selected).map((s) => s.id);

        if (JSON.stringify(currentStatusIds) !== JSON.stringify(newStatusIds)) {
            filterOptions.setOrderStatus(statusFromUrl);
        }

        const currentLocationIds = memoizedGetPrimarySelectedLocations;
        const newLocationIds = getPrimarySelectedLocations(locationFromUrl);

        if (JSON.stringify(currentLocationIds) !== JSON.stringify(newLocationIds)) {
            filterOptions.setLocationSelectedItems(locationFromUrl);
        }
    }, [location.search, buildingsData]);

    /**
     * Handles table sorting changes and updates the sorting state.
     * Maps UI sorting fields to API-compatible field names.
     *
     * @param e - Sorting change event from the table component
     */
    const onSortingChange: NonCancelableEventHandler<TableProps.SortingState<Order>> = async (e) => {
        const filterField = e.detail.sortingColumn.sortingField;
        const sortBy = SORT_FIELD_MAPPINGS[filterField as string] || filterField;
        const sortOrder = e.detail.isDescending ? "sort.desc" : "sort.asc";
        setCurrentSorting({ sortField: filterField, sortBy, sortOrder });
    };

    // Main filter effect - builds query and updates URL
    useEffect(() => {
        setFilterLoading(true);

        const queryObject = buildQueryObject(filterOptions, memoizedGetPrimarySelectedLocations, currentSorting);
        const urlParams = updateUrlParams(filterOptions, memoizedGetPrimarySelectedLocations, currentSorting);

        if (searchParams.toString() !== urlParams.toString()) {
            setSearchParams(urlParams);
        }

        setQuery(queryObject);
        setFilterLoading(false);
    }, [
        filterOptions.query,
        filterOptions.requesterQuery,
        filterOptions.orderStatus,
        filterOptions.locationSelectedItems,
        currentSorting,
    ]);

    return {
        filterOptions,
        sorting: {
            currentSorting,
            onSortingChange,
        },
        filter: {
            query,
            filterLoading,
        },
    };
};

/**
 * Main component for the Local IT Order List view.
 * Provides comprehensive order filtering, searching, and management interface.
 *
 * @returns JSX element containing the complete order list interface
 */
const LocalITOrderList = () => {
    /* Order Created Notification */
    const { Flashbar, generateMessage, generateError } = useFlashbar(true);

    useOrderResponse(generateMessage);

    /* Filtering */
    const orderFiltering = useFiltering();

    /**
     * Navigates to the new order creation page.
     */
    const navigate = useNavigate();

    const navigateToNewOrder = () => {
        navigate(Paths.itHardwareRestock.url);
    };

    /* APP LAYOUT CONFIGURATION */
    const contentType: AppLayoutProps.ContentType = "table";

    /* GRID DEFINITION */
    const gridDefinition: ReadonlyArray<GridProps.ElementDefinition> = [
        { colspan: { default: 3, xl: 2 } },
        { colspan: { default: 9, xl: 10 } },
    ];

    /* BREADCRUMBS */
    const breadcrumbs = {
        items: [{ text: Paths.itOrderList.name, href: Paths.itOrderList.url }],
    };

    return (
        <AppLayout
            navigationHide={true}
            toolsHide={true}
            notifications={Flashbar}
            breadcrumbs={<NavigationBreadcrumbs items={breadcrumbs.items} />}
            content={
                <CustomGridLayout
                    title={getString(`${getStringPath}.title`)}
                    actions={
                        <Button
                            variant="primary"
                            onClick={navigateToNewOrder}
                            data-testid={LOCAL_IT_ORDER_LIST.NEW_ORDER}
                        >
                            {getString(`${getStringPath}.newOrderButton`)}
                        </Button>
                    }
                    leftSection={
                        <Container>
                            <SpaceBetween size="m">
                                <CustomPropertyFilter
                                    label={getString(`${getStringPath}.searchOrders.label`)}
                                    placeholder={getString(`${getStringPath}.searchOrders.placeholder`)}
                                    testId={LOCAL_IT_ORDER_LIST.SEARCH_FOR}
                                    onFilterChange={({ detail }) => orderFiltering.filterOptions.setQuery(detail)}
                                    query={orderFiltering.filterOptions.query}
                                    filteringProperties={orderFiltering.filterOptions.filteringProperties}
                                    hideOperations={false}
                                    filteringOptions={orderFiltering.filterOptions.categoryOptions}
                                    touchpoint="intake-search-orders"
                                />
                                <CustomPropertyFilter
                                    label={getString(`${getStringPath}.requester.label`)}
                                    placeholder={getString(`${getStringPath}.requester.placeholder`)}
                                    testId={LOCAL_IT_ORDER_LIST.REQUESTER}
                                    onFilterChange={({ detail }) =>
                                        orderFiltering.filterOptions.setRequesterQuery(detail)
                                    }
                                    query={orderFiltering.filterOptions.requesterQuery}
                                    filteringProperties={orderFiltering.filterOptions.requesterFilteringProperty}
                                    hideOperations={true}
                                    info={
                                        <CustomPopOver
                                            content={
                                                <TextContent>
                                                    {getString(`${getStringPath}.requester.info`)}
                                                </TextContent>
                                            }
                                        />
                                    }
                                    touchpoint="intake-search-requesters"
                                />
                                <CheckboxFilter
                                    name={getString(`${getStringPath}.filters.orderStatus`)}
                                    checkBoxStructure={orderFiltering.filterOptions.orderStatus}
                                    setCheckBoxStructure={orderFiltering.filterOptions.setOrderStatus}
                                    testId={LOCAL_IT_ORDER_LIST.STATUS_FILTERS}
                                    touchpoint="intake-order-status"
                                />
                                <CheckboxFilter
                                    name={getString(`${getStringPath}.filters.locations`)}
                                    description={getString(`${getStringPath}.filters.locationsDescription`)}
                                    checkBoxStructure={orderFiltering.filterOptions.locationSelectedItems}
                                    setCheckBoxStructure={orderFiltering.filterOptions.setLocationSelectedItems}
                                    testId={LOCAL_IT_ORDER_LIST.LOCATION_FILTERS}
                                    touchpoint="intake-order-locations"
                                />
                            </SpaceBetween>
                        </Container>
                    }
                    rightSection={
                        <OrderItemsTable
                            generateError={generateError}
                            query={orderFiltering.filter.query}
                            isLoading={orderFiltering.filter.filterLoading}
                            sorting={orderFiltering.sorting.currentSorting}
                            onSortingChange={orderFiltering.sorting.onSortingChange}
                        />
                    }
                    gridDefinition={gridDefinition}
                />
            }
            contentType={contentType}
        />
    );
};

export default LocalITOrderList;
